/**
 * useMicDisplayState.ts
 * 
 * Custom hook to determine the display state of the microphone component 
 * based on various input parameters. This includes visibility, waveform 
 * display, and text messages.
 * 
 * Fairly complex set of rules; hence encapsulated here.
 */

import { useEffect, useReducer } from "react";
import i18n from "../../i18n/config";
import { useHasKeyboard } from "../../hooks/useHasKeyboard";
import { AudioMessage_ServerInfo_HealthStatus as HealthStatus } from "../../generated/audio_message";

// Constants
const DISCONNECTED_TIMEOUT_MS = 5000;

// Display state interface
export interface MicDisplayState {
  visible: boolean;
  showWaveform: boolean;
  text: string | null;
}

// Input parameters for determining display state
export interface MicDisplayParams {
  // Connection states
  socketConnected: boolean;
  pageLoadTime: number;
  healthStatus: HealthStatus;

  // Mic states
  active: boolean;
  midDeactivation: boolean;
  isInitializing: boolean;
  needsPermission: boolean;
  hasEverTransmitted: boolean;

  // Speech detection states
  isSilentWhileActive: boolean;
  isStillWithUs: boolean;
  isResumedTalking: boolean;
  isTalkingButNotTransmitting: boolean;

  // UI states
  isWindowFocused: boolean;
  isOpenMic: boolean;
  showLiveMicToggle: boolean;

  // Config settings
  micStaticText?: string;
  micVolatile?: boolean;
}

// Action types
type MicDisplayAction =
  | { type: 'UPDATE_DISPLAY'; params: MicDisplayParams; hasKeyboard: boolean }
  | { type: 'SET_SEVERELY_DEGRADED' };

// Helper function to check if health is degraded
function isHealthDegraded(status: HealthStatus): boolean {
  return status === HealthStatus.DEGRADED || status === HealthStatus.SEVERELY_DEGRADED;
}

// Initial state
const initialState: MicDisplayState = {
  visible: true,
  showWaveform: false,
  text: null,
};

// Reducer function
function micDisplayReducer(state: MicDisplayState, action: MicDisplayAction): MicDisplayState {
  switch (action.type) {
    case 'SET_SEVERELY_DEGRADED':
      // When severely degraded, hide everything
      return {
        visible: false,
        showWaveform: false,
        text: null,
      };

    case 'UPDATE_DISPLAY': {
      const params = action.params;
      const hasKeyboard = action.hasKeyboard;

      // Calculate visibility
      // If micVolatile is false, never hide the microphone
      const microphoneHidden = params.micVolatile !== false && (
        params.hasEverTransmitted &&
        params.isWindowFocused &&
        !params.isTalkingButNotTransmitting &&
        !params.active &&
        !params.needsPermission &&
        !params.isInitializing &&
        params.healthStatus !== HealthStatus.DEGRADED
      );

      // Calculate text to display
      let text: string | null = null;
      const timeSincePageLoad = Date.now() - params.pageLoadTime;

      // If static text is configured, always use it
      if (params.micStaticText) {
        text = params.micStaticText;
      } else {
        // Otherwise use dynamic text based on state
        if (params.socketConnected === false && timeSincePageLoad >= DISCONNECTED_TIMEOUT_MS) {
          text = i18n.t("micDisplay.disconnected");
        } else if (params.isOpenMic) {
          // In open mic mode, only show text if degraded
          if (isHealthDegraded(params.healthStatus)) {
            text = i18n.t("micDisplay.degraded");
          }
        } else if (params.active || params.midDeactivation) {
          // When active, show silence feedback messages
          if (params.isSilentWhileActive) {
            text = i18n.t("micDisplay.notHearing");
          } else if (params.isStillWithUs) {
            text = i18n.t("micDisplay.stillWithUs");
          } else if (params.isResumedTalking) {
            text = i18n.t("micDisplay.thereYouAre");
          }
          // Otherwise no text when active (waveform is showing)
        } else {
          // Fully inactive (neither active nor mid-deactivation)
          if (params.isInitializing) {
            text = i18n.t("micDisplay.initializing");
          } else if (params.needsPermission) {
            text = i18n.t("micDisplay.enableMic");
          } else if (isHealthDegraded(params.healthStatus)) {
            text = i18n.t("micDisplay.degraded");
          } else if (params.isWindowFocused) {
            text = hasKeyboard ? i18n.t("micDisplay.holdSpacebar") : i18n.t("micDisplay.pressAndHold");
          } else {
            text = i18n.t("micDisplay.tapWindow");
          }
        }
      }

      // Calculate waveform visibility
      let showWaveform = false;
      if (params.isOpenMic && !isHealthDegraded(params.healthStatus)) {
        showWaveform = true;
      } else if (params.active || params.midDeactivation) {
        // Show waveform when active unless we're showing silence feedback
        showWaveform = !params.isSilentWhileActive && !params.isStillWithUs && !params.isResumedTalking;
      }

      return {
        visible: !microphoneHidden,
        showWaveform,
        text,
      };
    }

    default:
      return state;
  }
}

// Custom hook
export function useMicDisplayState(params: MicDisplayParams): MicDisplayState {
  const hasKeyboard = useHasKeyboard();
  const [state, dispatch] = useReducer(micDisplayReducer, initialState);

  useEffect(() => {
    // Check for severely degraded health status first
    if (params.healthStatus === HealthStatus.SEVERELY_DEGRADED) {
      dispatch({ type: 'SET_SEVERELY_DEGRADED' });
    } else {
      dispatch({ type: 'UPDATE_DISPLAY', params, hasKeyboard });
    }
  }, [
    params.socketConnected,
    params.pageLoadTime,
    params.healthStatus,
    params.active,
    params.midDeactivation,
    params.isInitializing,
    params.needsPermission,
    params.hasEverTransmitted,
    params.isSilentWhileActive,
    params.isStillWithUs,
    params.isResumedTalking,
    params.isTalkingButNotTransmitting,
    params.isWindowFocused,
    params.isOpenMic,
    params.showLiveMicToggle,
  ]);

  return state;
}
